import React, { useState } from 'react';
import Paper from '@material-ui/core/Paper';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import { FormatDate, isEmpty } from '../../modules/appLibrary';

const DEFAULT_CARD_WIDTH = 261;
const DEFAULT_CARD_IMAGE_HEIGHT = 193.6;
const DEFAULT_CARD_MIN_HEIGHT = 387.2;

function resolveCssSize(value, fallback) {
    if (value === null || value === undefined || value === '') return fallback;
    if (typeof value === 'number' && Number.isFinite(value)) {
        return `${value}px`;
    }

    return String(value);
}

function resolveValue(item, keys) {
    if (isEmpty(item) || isEmpty(keys)) return '';

    for (let i = 0; i < keys.length; i++) {
        const key = keys[i];
        if (Object.prototype.hasOwnProperty.call(item, key) && !isEmpty(item[key])) {
            return item[key];
        }
    }

    return '';
}

function normalizeText(value) {
    if (isEmpty(value)) return '';
    return String(value).trim();
}

function isFlagOn(value) {
    const text = normalizeText(value).toLowerCase();
    return text === '1' || text === 'true' || text === 'on';
}

function normalizeImageSourceValue(value) {
    if (isEmpty(value)) return '';

    const text = String(value).trim();
    if (
        text.startsWith('data:') ||
        text.startsWith('http://') ||
        text.startsWith('https://') ||
        text.startsWith('/')
    ) {
        return text;
    }

    return '';
}

function resolveImageSource(item) {
    if (isEmpty(item)) return '';

    const directKeys = [
        'imageUrl',
        'imageSrc',
        'image',
        'pictureUrl',
        'pictureSrc',
        'PDT_PictureUrl',
        'PDT_PICTUREURL',
        'PDT_PictureFileUrl',
        'PDT_PICTUREFILEURL',
        'PDT_PictureBase64',
        'PDT_PICTUREBASE64'
    ];

    for (let i = 0; i < directKeys.length; i++) {
        const key = directKeys[i];
        if (Object.prototype.hasOwnProperty.call(item, key) && !isEmpty(item[key])) {
            const normalized = normalizeImageSourceValue(item[key]);
            if (!isEmpty(normalized)) return normalized;
        }
    }

    return normalizeImageSourceValue(resolveValue(item, ['PDT_PICTUREFILENM1', 'PDT_PictureFileNm1']));
}

function normalizeNumberText(value) {
    if (isEmpty(value)) return '';

    return String(value)
        .trim()
        .replace(/,/g, '')
        .replace(/[^\d.-]/g, '');
}

function formatIntegerValue(value, withYen = false) {
    const normalized = normalizeNumberText(value);
    if (isEmpty(normalized)) return '';

    const numberValue = Number(normalized);
    if (!Number.isFinite(numberValue)) {
        return String(value);
    }

    const formatted = new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 0 }).format(numberValue);
    return withYen ? `¥${formatted}` : formatted;
}

function formatDateValue(value) {
    if (isEmpty(value)) return '';

    if (typeof value === 'string') {
        return FormatDate(value);
    }

    if (value instanceof Date && !Number.isNaN(value.getTime())) {
        const y = value.getFullYear();
        const m = String(value.getMonth() + 1).padStart(2, '0');
        const d = String(value.getDate()).padStart(2, '0');
        return `${y}/${m}/${d}`;
    }

    return String(value);
}

function calculateCostRateValue(price, plannedCost) {
    const normalizedPrice = normalizeNumberText(price);
    const normalizedPlannedCost = normalizeNumberText(plannedCost);
    if (isEmpty(normalizedPrice) || isEmpty(normalizedPlannedCost)) return '';

    const priceNumber = Number(normalizedPrice);
    const plannedCostNumber = Number(normalizedPlannedCost);
    if (!Number.isFinite(priceNumber) || !Number.isFinite(plannedCostNumber) || plannedCostNumber === 0) {
        return '';
    }

    const rate = priceNumber / plannedCostNumber;
    return new Intl.NumberFormat('ja-JP', { maximumFractionDigits: 2 }).format(rate);
}

function MapCardField(props) {
    const align = props.align || 'left';
    const compact = Boolean(props.compact);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                flex: compact ? '0 0 auto' : 1,
                alignItems: align === 'right' ? 'flex-end' : 'flex-start'
            }}
        >
            <div
                style={{
                    color: '#8c8c8c',
                    fontSize: 9,
                    lineHeight: 1.05,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis'
                }}
            >
                {props.label}
            </div>
            <div
                style={{
                    marginTop: 1,
                    color: '#333',
                    fontSize: 11,
                    lineHeight: 1.1,
                    fontWeight: 600,
                    textAlign: align,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '100%'
                }}
            >
                {props.value}
            </div>
        </div>
    );
}

function MapCardRow(props) {
    return (
        <div
            style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                minWidth: 0
            }}
        >
            {props.children}
        </div>
    );
}

function MapCardInlineField(props) {
    const align = props.align || 'left';
    const label = isEmpty(props.label) ? '' : String(props.label);
    const value = isEmpty(props.value) ? '' : String(props.value);

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                minWidth: 0,
                justifyContent: 'flex-start',
                paddingLeft: align === 'right' ? 18 : 0,
                boxSizing: 'border-box'
            }}
        >
            <div
                style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    minWidth: 0,
                    maxWidth: '100%',
                    gap: 2
                }}
            >
                <span
                    style={{
                        color: '#8c8c8c',
                        fontSize: 9,
                        lineHeight: 1.05,
                        whiteSpace: 'nowrap',
                        flex: '0 0 auto'
                    }}
                >
                    {label}{isEmpty(label) ? '' : '：'}
                </span>
                <span
                    style={{
                        color: '#333',
                        fontSize: 11,
                        lineHeight: 1.1,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        minWidth: 0,
                        flex: '0 1 auto'
                    }}
                >
                    {value}
                </span>
            </div>
        </div>
    );
}

function MapCardInlineRow(props) {
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                columnGap: 8,
                alignItems: 'flex-start',
                minWidth: 0,
                width: '100%'
            }}
        >
            {props.children}
        </div>
    );
}

function getCardCode(item) {
    return resolveValue(item, ['PDT_SYOHINCD', 'PDT_SyohinCD', 'PDT_HINBAN', 'hinban', 'productCode']);
}

function getProductName(item) {
    return resolveValue(item, ['PDT_HINMEINM', 'PDT_HinmeiNm', 'name', 'productName']);
}

function resolveKakousijiKubunLabel(value, item) {
    const displayName = resolveValue(item, ['SSDX_KBN_nm', 'SSDX_KBN_Nm', 'SSDX_KBN_NAME', 'SSDX_KbnNm']);
    if (!isEmpty(displayName)) {
        return displayName;
    }

    const normalizedValue = normalizeText(value);
    if (normalizedValue === '1') return '量産';
    if (normalizedValue === '2') return '追加';
    if (normalizedValue === '0') return 'サンプル';
    return normalizedValue;
}

function isConfirmedMapCard(item) {
    if (isFlagOn(resolveValue(item, ['SSD_KANRYOFLG', 'SSD_KanryoFlg']))) {
        return true;
    }

    return !isEmpty(resolveValue(item, ['SSD_SEIHINHACHUDENNO', 'SSD_SeihinHachuDenno']));
}

function isIssuedMapCard(item) {
    return !isEmpty(resolveValue(item, ['SSD_SEIHINHACHUDENNO', 'SSD_SeihinHachuDenno']));
}

function isConfirmedHinban(item) {
    return isFlagOn(resolveValue(item, ['MAPCARD_HINBAN_CONFIRMED', 'MapCardHinbanConfirmed']));
}

function getDisplayCardCode(item) {
    const productCode = getCardCode(item);
    if (!isConfirmedMapCard(item)) {
        return productCode;
    }

    const kbn = normalizeText(resolveValue(item, ['SSDX_KBN', 'SSDX_Kbn']));
    const processNo = normalizeText(resolveValue(item, ['SSD_NO']));
    const confirmedCode = [kbn, processNo].filter((value) => !isEmpty(value)).join('');
    return isEmpty(confirmedCode) ? productCode : confirmedCode;
}

function getMapCardStatusLabel(item) {
    return isConfirmedMapCard(item) ? '確定済み' : '未確定';
}

function getCardKey(item) {
    const keys = [
        normalizeText(resolveValue(item, ['SSD_NO'])),
        normalizeText(getCardCode(item)),
        normalizeText(resolveValue(item, ['SSDX_KIKAKUNO'])),
        normalizeText(resolveValue(item, ['SSDX_SHOPYMD'])),
        normalizeText(resolveValue(item, ['SSDM_COLORCD'])),
        normalizeText(resolveValue(item, ['SSDM_SIZECD']))
    ].filter((value) => !isEmpty(value));

    return keys.length === 0 ? '' : keys.join('|');
}

export default function TenkaiMdBoardMapCard(props) {
    const item = props.item || {};
    const cardWidth = resolveCssSize(props.width, `${DEFAULT_CARD_WIDTH}px`);
    const cardMinWidth = resolveCssSize(props.minWidth, cardWidth);
    const cardMinHeight = resolveCssSize(props.minHeight, `${DEFAULT_CARD_MIN_HEIGHT}px`);
    const cardImageHeight = resolveCssSize(props.imageHeight, `${DEFAULT_CARD_IMAGE_HEIGHT}px`);
    const imageSrc = resolveImageSource(item);
    const code = getCardCode(item);
    const productName = getProductName(item);
    const planNo = resolveValue(item, ['SSDX_KIKAKUNO']);
    const price = resolveValue(item, ['SSDX_PRICE']);
    const siire = resolveValue(item, ['SSD_SIIRECD']);
    const kakou = resolveValue(item, ['SSD_KAKOUSAKICD']);
    const centerDate = formatDateValue(resolveValue(item, ['SSDNY_CENTER']));
    const inputCount = formatIntegerValue(resolveValue(item, ['SSDM_MDHATCHUSU']));
    const plannedCostRaw = resolveValue(item, ['PDTMD_YOTEIGENKA', 'PDT_KARI_GENKA', 'PDTMD_KARI_GENKA']);
    const plannedCost = formatIntegerValue(plannedCostRaw, true);
    const costRate = calculateCostRateValue(price, plannedCostRaw);
    const color = resolveValue(item, ['SSDM_COLORCD']);
    const size = resolveValue(item, ['SSDM_SIZECD']);
    const kakousijiKubunValue = resolveValue(item, ['SSDX_KBN', 'SSDX_Kbn']);
    const kakousijiKubunLabel = resolveKakousijiKubunLabel(kakousijiKubunValue, item);
    const kakousijiNo = resolveValue(item, ['SSDX_NO', 'SSD_NO']);
    const isHinbanConfirmed = isConfirmedHinban(item);
    const isStopped = isFlagOn(resolveValue(item, ['PDT_STOPFLG', 'PDT_StopFlg', 'SSD_DISFLG', 'SSD_DisFlg']));
    const isIssued = isIssuedMapCard(item);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);

    function closeMenu() {
        setMenuAnchorEl(null);
    }

    function openMenu(e) {
        e.preventDefault();
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    }

    function handleOpenMaster(e) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        closeMenu();
        if (typeof props.onOpenMaster === 'function') {
            props.onOpenMaster(item);
        }
    }

    function handleOpenProductSub(e) {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        closeMenu();
        if (typeof props.onOpenProductSub === 'function') {
            props.onOpenProductSub(item);
        }
    }

    function handleToggleStop(stopOn) {
        closeMenu();
        if (typeof props.onToggleStop === 'function') {
            props.onToggleStop(item, stopOn);
        }
    }

    function handleDelete() {
        closeMenu();
        if (typeof props.onDelete === 'function') {
            props.onDelete(item);
        }
    }

    function handleDragStart(event) {
        if (typeof props.onDragStart === 'function') {
            props.onDragStart(item, props.source || 'board', event);
        }
    }

    function handleDragEnd(event) {
        if (typeof props.onDragEnd === 'function') {
            props.onDragEnd(item, props.source || 'board', event);
        }
    }

    function handleCardClick(e) {
        if (!e || Number(e.detail) !== 2) {
            return;
        }

        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        handleOpenProductSub();
    }

    const menuItems = isStopped
        ? [
            { key: 'open-master', label: '品番マスタ起動', icon: 'description', action: handleOpenMaster },
            { key: 'restore', label: 'MAPカードを復活', icon: 'restore', action: () => handleToggleStop(false) },
            { key: 'delete', label: 'MAPカードの削除', icon: 'delete_outline', action: handleDelete }
        ]
        : [
            { key: 'open-master', label: '品番マスタ起動', icon: 'description', action: handleOpenMaster },
            { key: 'stop', label: 'MAPカードを中止', icon: 'cancel', action: () => handleToggleStop(true), disabled: isIssued },
            { key: 'delete', label: 'MAPカードの削除', icon: 'delete_outline', action: handleDelete }
        ];


    const key = getCardKey(item);
    const draggable = props.draggable !== false;
    const productLabel = productName;

    return (
        <div
            style={{
                width: cardWidth,
                minWidth: cardMinWidth,
                flex: `0 0 ${cardWidth}`,
                boxSizing: 'border-box'
            }}
        >
            <Paper
                elevation={0}
                draggable={draggable}
                onDragStart={draggable ? handleDragStart : undefined}
                onDragEnd={draggable ? handleDragEnd : undefined}
                onClick={handleCardClick}
                style={{
                    width: '100%',
                    minHeight: cardMinHeight,
                    border: '1px solid #c8c8c8',
                    boxShadow: 'none',
                    backgroundColor: '#fff',
                    borderRadius: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                    cursor: draggable ? 'grab' : 'pointer',
                    userSelect: 'none'
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        minHeight: 20,
                        backgroundColor: '#a8b5b0',
                        borderBottom: '1px solid #c8d0cc',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 6px 0 8px'
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            color: '#fff',
                            fontSize: 12,
                            fontWeight: 700,
                            lineHeight: 1,
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            paddingRight: 22
                        }}
                    >
                        {code}
                    </div>
                    <IconButton
                        size="small"
                        onClick={openMenu}
                        style={{
                            position: 'absolute',
                            right: 0,
                            top: -1,
                            width: 22,
                            height: 22,
                            color: '#333'
                        }}
                    >
                        <Icon style={{ fontSize: 16 }}>more_vert</Icon>
                    </IconButton>
                    <Menu
                        anchorEl={menuAnchorEl}
                        open={Boolean(menuAnchorEl)}
                        onClose={closeMenu}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                        PaperProps={{
                            style: {
                                minWidth: 184
                            }
                        }}
                    >
                        {menuItems.map((menuItem) => (
                            <MenuItem
                                key={`${key}-${menuItem.key}`}
                                onClick={menuItem.action}
                                disabled={Boolean(menuItem.disabled)}
                                style={{ fontSize: 12, minHeight: 32 }}
                            >
                                <ListItemIcon style={{ minWidth: 30 }}>
                                    <Icon style={{ fontSize: 18 }}>{menuItem.icon}</Icon>
                                </ListItemIcon>
                                {menuItem.label}
                            </MenuItem>
                        ))}
                    </Menu>
                </div>

                <div
                    style={{
                        minHeight: 28,
                        borderBottom: '1px solid #dfe4e2',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#333',
                        fontSize: 13,
                        fontWeight: 600,
                        lineHeight: 1,
                        padding: '2px 8px 3px',
                        boxSizing: 'border-box'
                    }}
                >
                    {productLabel}
                </div>

                <div
                    style={{
                        margin: '8px 8px 0',
                        height: cardImageHeight,
                        border: '1px solid #d7d7d7',
                        backgroundColor: '#f3f3f3',
                        boxSizing: 'border-box',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative'
                    }}
                >
                    {isEmpty(imageSrc) ? (
                        <div
                            style={{
                                color: '#7f7f7f',
                                fontFamily: '"Times New Roman", serif',
                                fontSize: 22,
                                fontWeight: 700,
                                letterSpacing: 0.2
                            }}
                        >
                            no image
                        </div>
                    ) : (
                        <img
                            src={imageSrc}
                            alt={code || 'map-card'}
                            style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                            }}
                        />
                    )}

                    {isStopped ? (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                                pointerEvents: 'none'
                            }}
                        >
                            <div
                                style={{
                                    color: '#f4ea00',
                                    display: 'none',
                                    fontSize: 28,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    textShadow: '0 0 3px rgba(0, 0, 0, 0.85)'
                                }}
                            >
                                鬯ｯ・ｯ繝ｻ・ｯ郢晢ｽｻ繝ｻ・ｯ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｮ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・｣鬯ｯ・ｮ繝ｻ・ｯ髯ｷ闌ｨ・ｽ・ｷ郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・ｹ鬯ｩ蟷｢・ｽ・｢髫ｴ雜｣・ｽ・｢郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・ｻ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ鬯ｩ蟷｢・ｽ・｢髫ｴ雜｣・ｽ・｢郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・ｻ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｳ鬯ｯ・ｯ繝ｻ・ｩ髯晢ｽｷ繝ｻ・｢郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・｢鬯ｮ・ｫ繝ｻ・ｴ鬮ｮ諛ｶ・ｽ・｣郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・｢鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｻ鬯ｯ・ｩ陝ｷ・｢繝ｻ・ｽ繝ｻ・｢鬮ｫ・ｴ髮懶ｽ｣繝ｻ・ｽ繝ｻ・｢驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｻ鬯ｩ蟷｢・ｽ・｢髫ｴ雜｣・ｽ・｢郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・ｻ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｭ鬯ｯ・ｯ繝ｻ・ｯ郢晢ｽｻ繝ｻ・ｯ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｮ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｮ鬯ｯ・ｮ繝ｻ・ｯ髮矩醍袖繝ｻ・ｱ陞｢・ｹ郢晢ｽｻ郢晢ｽｻ繝ｻ・ｽ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｱ鬯ｯ・ｩ陝ｷ・｢繝ｻ・ｽ繝ｻ・｢鬮ｫ・ｴ髮懶ｽ｣繝ｻ・ｽ繝ｻ・｢驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｻ鬯ｯ・ｩ陝ｷ・｢繝ｻ・ｽ繝ｻ・｢鬮ｫ・ｴ髮懶ｽ｣繝ｻ・ｽ繝ｻ・｢驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｻ鬯ｩ蟷｢・ｽ・｢髫ｴ雜｣・ｽ・｢郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・ｻ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ鬯ｯ・ｩ陝ｷ・｢繝ｻ・ｽ繝ｻ・｢鬮ｫ・ｴ髮懶ｽ｣繝ｻ・ｽ繝ｻ・｢驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｽ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・ｻ鬯ｩ蟷｢・ｽ・｢髫ｴ雜｣・ｽ・｢郢晢ｽｻ繝ｻ・ｽ郢晢ｽｻ繝ｻ・ｻ鬩幢ｽ｢隴趣ｽ｢繝ｻ・ｽ繝ｻ・ｻ驛｢譎｢・ｽ・ｻ郢晢ｽｻ繝ｻ・｢
                            </div>
                            <div
                                style={{
                                    color: '#f4ea00',
                                    fontSize: 28,
                                    fontWeight: 900,
                                    lineHeight: 1,
                                    textShadow: '0 0 3px rgba(0, 0, 0, 0.85)'
                                }}
                            >
                                {'中止'}
                            </div>
                        </div>
                    ) : null}
                </div>

                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        padding: '8px 8px 7px',
                        boxSizing: 'border-box',
                        display: 'flex'
                    }}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1, minHeight: 0 }}>
                        <MapCardInlineRow>
                            <MapCardInlineField label={"企画NO"} value={planNo} />
                            <MapCardInlineField label={"上代"} value={formatIntegerValue(price, true)} align='right' />
                        </MapCardInlineRow>
                        <MapCardInlineRow>
                            <MapCardInlineField label={"仕入先"} value={siire} />
                            <MapCardInlineField label={"加工先"} value={kakou} align='right' />
                        </MapCardInlineRow>
                        <MapCardInlineRow>
                            <MapCardInlineField label={"センター納品日"} value={centerDate} />
                            <MapCardInlineField label={"投入数"} value={inputCount} align='right' />
                        </MapCardInlineRow>
                        <MapCardInlineRow>
                            <MapCardInlineField label={"原価"} value={plannedCost} />
                            <MapCardInlineField label={"原価率"} value={costRate} align='right' />
                        </MapCardInlineRow>
                        <MapCardInlineRow>
                            <MapCardInlineField label={"カラー"} value={color} />
                            <span />
                        </MapCardInlineRow>
                        <MapCardInlineRow>
                            <MapCardInlineField label={"サイズ"} value={size} />
                            <span />
                        </MapCardInlineRow>
                        {isHinbanConfirmed ? (
                            <div style={{ marginTop: 'auto' }}>
                                <MapCardInlineRow>
                                    <span />
                                    <MapCardInlineField label={kakousijiKubunLabel || "加工指示区分"} value={kakousijiNo} align='right' />
                                </MapCardInlineRow>
                            </div>
                        ) : null}
                    </div>
                </div>
            </Paper>
        </div>
    );
}

